<script>
import {
    MinLengthValidator,
    MaxLengthValidator,
    LengthValidator,
    EmailValidator,
    RegularExpressionValidator,
    NotEmptyValidator,
    DateValidator,
    NumberValidator
} from "$vueCommon/classes/Validator.js";
import Validator from "$vueCommon/classes/Validator.js";
import SelectCountry from "$vueCommon/components/Select/SelectCountry.vue";
import SelectPhone from "$vueCommon/components/Select/SelectPhone.vue";
import CheckoutAPI from "$vueCommon/classes/API/CheckoutAPI.js";
import Toast from "$vueCommon/components/Toast.vue";
import {ToastMessageDanger, ToastMessageSuccess, ToastMessageWarning} from "$vueCommon/classes/ToastMessage.js";
import eventBus from "vue3-eventbus";
import { VueDatePicker } from "@vuepic/vue-datepicker";

const LETTERS =
    "A-Za-z" +
    "\\u00C0-\\u02FF" +
    "\\u0370-\\u03FF" +
    "\\u0400-\\u04FF" +
    "\\u0590-\\u05FF" +
    "\\u0600-\\u06FF" +
    "\\u0900-\\u097F" +
    "\\u0980-\\u09FF" +
    "\\u0A00-\\u0A7F" +
    "\\u0A80-\\u0AFF" +
    "\\u0B00-\\u0B7F" +
    "\\u0B80-\\u0BFF" +
    "\\u0C00-\\u0C7F" +
    "\\u0C80-\\u0CFF" +
    "\\u0D00-\\u0D7F" +
    "\\u0E00-\\u0E7F" +
    "\\u0E80-\\u0EFF" +
    "\\u10A0-\\u10FF" +
    "\\u1100-\\u11FF" +
    "\\u3040-\\u309F" +
    "\\u30A0-\\u30FF" +
    "\\u4E00-\\u9FFF";
const LETTER_CLASS = `[${LETTERS}]`;
const LETTER_OR_DIGIT_CLASS = `[${LETTERS}0-9]`;
const NAME_REGEX_JS = `^(?=.{1,255}$)(?=.*${LETTER_CLASS})${LETTER_CLASS}[${LETTERS}\\s\\-\\'.’]*$`;
const ADDRESS_LINE_REGEX_JS = `^(?=.*${LETTER_OR_DIGIT_CLASS})[ ${LETTERS}0-9\\-\\/\\.,'#()]+$`;
const ADDRESS_UNIT_REGEX_JS = `^(?=.*${LETTER_OR_DIGIT_CLASS})[ ${LETTERS}0-9\\-\\/\\.,'#]+$`;
const CITY_DISTRICT_REGEX_JS = `^(?=.*${LETTER_CLASS})[ ${LETTERS}\\-'\\.]+$`;
const POSTAL_GLOBAL_REGEX_JS = `^(?=.*${LETTER_OR_DIGIT_CLASS})[ ${LETTERS}0-9\\-]+$`;
const POSTAL_PL_REGEX_JS = "^\\d{2}-\\d{3}$";

export default {
    components: {Toast, SelectCountry, SelectPhone, VueDatePicker},
    props: {
        identity: Object,
        onConfirm: Function,
        onDismiss: Function,
        identityType: String,
        actionType: String,
        requiredFields: {
            type: Array,
            default: () => []
        }
    },
    mounted() {
        if (this.actionType === 'firstAdd' && this.identity && this.identity.id === null) {
            this.localIdentity = this.identity;
        } else {
            // dla zwykłego add/edit robimy kopię (żeby móc anulować)
            this.localIdentity = {...this.identity};
        }

        if (this.localIdentity && this.localIdentity.country_id != null && String(this.localIdentity.country_id) !== '') {
            this.localIdentity.country_id = Number(this.localIdentity.country_id);
        }

        if (this.localIdentity && this.localIdentity.pob != null && String(this.localIdentity.pob) !== '') {
            this.localIdentity.pob = Number(this.localIdentity.pob);
        }

        this.validator
            // name
            .add('name', (new MinLengthValidator(this, 'localIdentity.name', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('checkout.identity_name')})))
            .add('name', (new MaxLengthValidator(this, 'localIdentity.name', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('checkout.identity_name'), max:'255'})))

            // first_name (required|string|max:255|regex:NAME)
            .add('first_name', (new MinLengthValidator(this, 'localIdentity.first_name', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.first_name')})))
            .add('first_name', (new MaxLengthValidator(this, 'localIdentity.first_name', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.first_name'), max:'255'})))
            .add('first_name', (new RegularExpressionValidator(this, 'localIdentity.first_name', NAME_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.first_name')})))

            // last_name (required|string|max:255|regex:NAME)
            .add('last_name', (new MinLengthValidator(this, 'localIdentity.last_name', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.last_name')})))
            .add('last_name', (new MaxLengthValidator(this, 'localIdentity.last_name', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.last_name'), max:'255'})))
            .add('last_name', (new RegularExpressionValidator(this, 'localIdentity.last_name', NAME_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.last_name')})))

            // address (required|string|max:255|regex:ADDRESS_LINE)
            .add('address', (new MinLengthValidator(this, 'localIdentity.address', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.address_street')})))
            .add('address', (new MaxLengthValidator(this, 'localIdentity.address', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.address_street'), max:'255'})))
            .add('address', (new RegularExpressionValidator(this, 'localIdentity.address', ADDRESS_LINE_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.address_street')})))

            // address2 (nullable|string|max:60|regex:ADDRESS_UNIT)
            .add('address2', (new MaxLengthValidator(this, 'localIdentity.address2', 60))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.address_house_apartment_number'), max:'60'})))
            .add('address2', (new RegularExpressionValidator(this, 'localIdentity.address2', ADDRESS_UNIT_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.address_house_apartment_number')}))
                .setCondition(() => !!(this.localIdentity.address2 && String(this.localIdentity.address2).trim().length)))

            // district (required|string|max:255|regex:CITY_DISTRICT)
            .add('district', (new MinLengthValidator(this, 'localIdentity.district', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.district')})))
            .add('district', (new MaxLengthValidator(this, 'localIdentity.district', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.district'), max:'255'})))
            .add('district', (new RegularExpressionValidator(this, 'localIdentity.district', CITY_DISTRICT_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.district')})))

            // city (required|string|max:255|regex:CITY_DISTRICT)
            .add('city', (new MinLengthValidator(this, 'localIdentity.city', 1))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.city')})))
            .add('city', (new MaxLengthValidator(this, 'localIdentity.city', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.city'), max:'255'})))
            .add('city', (new RegularExpressionValidator(this, 'localIdentity.city', CITY_DISTRICT_REGEX_JS, 'u'))
                .setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.city')})))

            // country_id (required|exists:countries,id)
            .add('country_id', (new NotEmptyValidator(this, 'localIdentity.country_id'))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.country')})))

            // phone (+00.000000...)
            .add('phone', (new RegularExpressionValidator(this, 'localIdentity.phone', '^\\+[0-9]{1,4}\\.[0-9]{6,15}$'))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.phone')})))

            // email (required/nullable|string|email:filter|max:255)
            .add('email', (new MaxLengthValidator(this, 'localIdentity.email', 255))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.email_address'), max:'255'})))
            .add('email', (new EmailValidator(this, 'localIdentity.email', false))
                .setMessage(this.$translate('validation.email', {attribute:this.$translate('auth.email_address')}))
                .setCondition(() => !!(this.localIdentity.email && String(this.localIdentity.email).trim().length)));

        this.isCompanyAddress = this.isTaxIdOrCompanyNameSet;
        this.setVariableValidators();
    },
    data() {
        return {
            localIdentity: {},
            validator: new Validator(),
            touched: {
                prefix: false,
                phone: false
            },
            saving: false,
            taxRateTimeout: null,
            isCompanyAddress: false,
            backendErrors: {}
        }
    },
    methods: {
        setVariableValidators() {
            this.validator.delete('company_name');
            this.validator.delete('tax_id');
            this.validator.delete('dob');
            this.validator.delete('pob');
            this.validator.delete('id_number');
            this.validator.delete('postal_code');

            if (this.isCompanyAddress) {
                this.validator.add('company_name', (new LengthValidator(this, 'localIdentity.company_name', 1, 255))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.company_name')})));
                this.validator.add('tax_id', (new LengthValidator(this, 'localIdentity.tax_id', 1, 255))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.tax_id')})));
            } else {
                this.validator.add('company_name', (new MaxLengthValidator(this, 'localIdentity.company_name', 255))
                    .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.company_name'), max:'255'})));
                this.validator.add('tax_id', (new MaxLengthValidator(this, 'localIdentity.tax_id', 255))
                    .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.tax_id'), max:'255'})));

                this.validator.add('company_name', (new NotEmptyValidator(this, 'localIdentity.company_name', true))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.company_name')}))
                    .setCondition(() => !!(this.localIdentity.tax_id && String(this.localIdentity.tax_id).trim().length)));

                this.validator.add('tax_id', (new NotEmptyValidator(this, 'localIdentity.tax_id', true))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.tax_id')}))
                    .setCondition(() => !!(this.localIdentity.company_name && String(this.localIdentity.company_name).trim().length)));
            }

            if (this.showField('dob')) {
                this.validator.add('dob', (new DateValidator(this, 'localIdentity.dob', false))
                    .setMessage(this.$translate('validation.date_of_birth_must_be_in_format', {format:'YYYY-MM-DD', example_format:'1990-01-01'})));
            }

            if (this.showField('pob')) {
                this.validator.add('pob', (new NumberValidator(this, 'localIdentity.pob', false))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('manager-settings.place_of_birth')})));
            }

            if (this.showField('id_number')) {
                this.validator.add('id_number', (new MaxLengthValidator(this, 'localIdentity.id_number', 255))
                    .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('manager-settings.id_number'), max:'255'})));
                this.validator.add('id_number', (new NotEmptyValidator(this, 'localIdentity.id_number', true))
                    .setMessage(this.$translate('validation.required', {attribute:this.$translate('manager-settings.id_number')})));
            }

            const isPL = parseInt(this.localIdentity.country_id) === 180;

            this.validator.add('postal_code', (new MinLengthValidator(this, 'localIdentity.postal_code', isPL ? 6 : 2))
                .setMessage(this.$translate('validation.required', {attribute:this.$translate('auth.postal_code')})));

            this.validator.add('postal_code', (new MaxLengthValidator(this, 'localIdentity.postal_code', isPL ? 6 : 14))
                .setMessage(this.$translate('validation.max.string', {attribute:this.$translate('auth.postal_code'), max: (isPL ? '6' : '14')})));

            this.validator.add('postal_code', (new RegularExpressionValidator(
                this,
                'localIdentity.postal_code',
                isPL ? POSTAL_PL_REGEX_JS : POSTAL_GLOBAL_REGEX_JS,
                isPL ? undefined : 'u'
            )).setMessage(this.$translate('validation.regex', {attribute: this.$translate('auth.postal_code')})));
        },
        onTaxRelatedFieldChanged() {
            if (this.actionType !== 'firstAdd') {
                return;
            }

            const i = this.localIdentity || {};

            if (!i.country_id) {
                return;
            }

            const hasCompanyOrTax = !!(i.company_name && i.company_name.trim().length > 0)
                || !!(i.tax_id && i.tax_id.trim().length > 0);

            if (hasCompanyOrTax) {
                if (!(i.company_name && i.company_name.trim().length > 0)
                    || !(i.tax_id && i.tax_id.trim().length > 0)
                ) {
                    return;
                }
            }

            if (this.taxRateTimeout) {
                clearTimeout(this.taxRateTimeout);
            }

            this.taxRateTimeout = setTimeout(() => {
                this.saving = true;

                CheckoutAPI.setTaxRate(i.country_id, i.company_name, i.tax_id)
                    .then(() => {})
                    .finally(() => {
                        this.saving = false;
                        this.taxRateTimeout = null;
                    });
            }, 500);
        },
        selectCountry(item) {
            if (!item || item.id == null) return;

            const newId = Number(item.id);
            const currId = this.localIdentity.country_id == null ? null : Number(this.localIdentity.country_id);

            if (currId === newId) {
                return;
            }

            this.validator.setTouched('country_id');
            this.localIdentity.country_id = newId;

            this.setVariableValidators();
        },
        selectPob(item) {
            if (!item || item.id == null) return;

            const newId = Number(item.id);
            const currId = this.localIdentity.pob == null ? null : Number(this.localIdentity.pob);

            if (currId === newId) {
                return;
            }

            this.validator.setTouched('pob');
            this.localIdentity.pob = newId;
        },
        updatePhone(e) {
            const newPhone = (e && (e.fullNumber ?? e.phone) != null) ? String(e.fullNumber ?? e.phone) : '';
            const newPrefix = (e && e.prefix != null) ? String(e.prefix) : '';

            const currPhone = this.localIdentity.phone == null ? '' : String(this.localIdentity.phone);
            const currPrefix = this.localIdentity.phone_prefix == null ? '' : String(this.localIdentity.phone_prefix);

            if (newPhone === currPhone && newPrefix === currPrefix) {
                return;
            }

            this.localIdentity.phone = newPhone;
            this.localIdentity.phone_prefix = newPrefix;
        },
        onPhoneTouched(part) {
            this.touched[part] = true;
            if (this.touched.prefix && this.touched.phone) {
                this.validator.setTouched('phone');
                this.validator.setTouched('phone_prefix');
            }
        },
        save() {
            this.backendErrors = {};

            this.setVariableValidators();

            if (!this.validator.validate()) {
                [
                    'name',
                    'first_name',
                    'last_name',
                    'company_name',
                    'tax_id',
                    'address',
                    'address2',
                    'district',
                    'postal_code',
                    'city',
                    'country_id',
                    'phone_prefix',
                    'phone',
                    'email',
                    'id_number',
                    'dob',
                    'pob'
                ].forEach(field => this.validator.setTouched(field));

                ToastMessageWarning.emit(this.$translate('checkout.please_correct_errors_in_form'), 1500);

                return Promise.resolve(false);
            }

            this.saving = true;

            return CheckoutAPI.postSaveUserIdentity(this.localIdentity).then((response) => {
                eventBus.emit('refreshIdentity', response);
                this.onConfirm(response.id);
                this.saving = false;

                ToastMessageSuccess.emit(this.$translate('checkout.additional_identity_has_been_saved'));

                return true;
            }, (errorResponse) => {
                this.saving = false;

                if (errorResponse.response && errorResponse.response.status === 422) {
                    const backendErrors = errorResponse.response.data.errors || {};

                    this.backendErrors = backendErrors;

                    Object.keys(backendErrors).forEach(field => {
                        this.validator.setTouched(field);
                    });

                    ToastMessageWarning.emit(this.$translate('checkout.please_correct_errors_in_form'), 1500);
                } else {
                    ToastMessageDanger.emit(this.$translate('domains.unexpected_error_occurred'));
                }

                return false;
            });
        },
        allMessages(field) {
            const validatorMessages = this.validator.getMessages(field).map(m => m.message);
            const backendMessages = this.backendErrors[field] || [];
            return [...validatorMessages, ...backendMessages];
        },
        async saveForPayNow() {
            this.backendErrors = {};

            this.setVariableValidators();

            if (!this.validator.validate()) {
                [
                    'name',
                    'first_name',
                    'last_name',
                    'company_name',
                    'tax_id',
                    'address',
                    'address2',
                    'district',
                    'postal_code',
                    'city',
                    'country_id',
                    'phone_prefix',
                    'phone',
                    'email',
                    'id_number',
                    'dob',
                    'pob'
                ].forEach(field => this.validator.setTouched(field));

                // ToastMessageWarning.emit(this.$translate('checkout.please_correct_errors_in_form'), 1500);

                return false;
            }

            this.saving = true;

            try {
                const response = await CheckoutAPI.postSaveUserIdentity(this.localIdentity);

                eventBus.emit('refreshIdentity', response);
                this.onConfirm(response.id);

                this.saving = false;

                return true;
            } catch (e) {
                this.saving = false;

                ToastMessageDanger.emit(this.$translate('domains.unexpected_error_occurred'));

                return false;
            }
        },
        showField(field) {
            if (this.identityType === 'billing') {
                if (['id_number', 'dob', 'pob'].includes(field)) {
                    return false;
                }

                return true;
            }

            if (this.identityType === 'registrant') {
                if (!['id_number', 'dob', 'pob'].includes(field)) {
                    return true;
                }

                if (this.isCompanyAddress) {
                    return false;
                }

                return Array.isArray(this.requiredFields) && this.requiredFields.includes(field);
            }

            return true;
        },
        toggleCompanyAddress() {
            if (!this.isCompanyAddress) {
                this.localIdentity.company_name = '';
                this.localIdentity.tax_id = '';
            }
            this.setVariableValidators();
        }
    },
    computed: {
        isValidName() {
            const hasBackend = this.backendErrors.name && this.backendErrors.name.length;
            return (this.validator.isValid('name') && !hasBackend) || !this.validator.isTouched('name');
        },
        isValidFirstName() {
            const hasBackend = this.backendErrors.first_name && this.backendErrors.first_name.length;
            return (this.validator.isValid('first_name') && !hasBackend) || !this.validator.isTouched('first_name');
        },
        isValidLastName() {
            const hasBackend = this.backendErrors.last_name && this.backendErrors.last_name.length;
            return (this.validator.isValid('last_name') && !hasBackend) || !this.validator.isTouched('last_name');
        },
        isValidTaxId() {
            const hasBackend = this.backendErrors.tax_id && this.backendErrors.tax_id.length;
            return (this.validator.isValid('tax_id') && !hasBackend) || !this.validator.isTouched('tax_id');
        },
        isValidCompanyName() {
            const hasBackend = this.backendErrors.company_name && this.backendErrors.company_name.length;
            return (this.validator.isValid('company_name') && !hasBackend) || !this.validator.isTouched('company_name');
        },
        isValidAddress() {
            const hasBackend = this.backendErrors.address && this.backendErrors.address.length;
            return (this.validator.isValid('address') && !hasBackend) || !this.validator.isTouched('address');
        },
        isValidAddress2() {
            const hasBackend = this.backendErrors.address2 && this.backendErrors.address2.length;
            return (this.validator.isValid('address2') && !hasBackend) || !this.validator.isTouched('address2');
        },
        isValidDistrict() {
            const hasBackend = this.backendErrors.district && this.backendErrors.district.length;
            return (this.validator.isValid('district') && !hasBackend) || !this.validator.isTouched('district');
        },
        isValidPostalCode() {
            const hasBackend = this.backendErrors.postal_code && this.backendErrors.postal_code.length;
            return (this.validator.isValid('postal_code') && !hasBackend) || !this.validator.isTouched('postal_code');
        },
        isValidCity() {
            const hasBackend = this.backendErrors.city && this.backendErrors.city.length;
            return (this.validator.isValid('city') && !hasBackend) || !this.validator.isTouched('city');
        },
        isValidCountryId() {
            const hasBackend = this.backendErrors.country_id && this.backendErrors.country_id.length;
            return (this.validator.isValid('country_id') && !hasBackend) || !this.validator.isTouched('country_id');
        },
        isValidIdNumber() {
            const hasBackend = this.backendErrors.id_number && this.backendErrors.id_number.length;
            return (this.validator.isValid('id_number') && !hasBackend) || !this.validator.isTouched('id_number');
        },
        isValidDob() {
            const hasBackend = this.backendErrors.dob && this.backendErrors.dob.length;
            return (this.validator.isValid('dob') && !hasBackend) || !this.validator.isTouched('dob');
        },
        isValidPob() {
            const hasBackend = this.backendErrors.pob && this.backendErrors.pob.length;
            return (this.validator.isValid('pob') && !hasBackend) || !this.validator.isTouched('pob');
        },
        isValidEmail() {
            const hasBackend = this.backendErrors.email && this.backendErrors.email.length;
            return (this.validator.isValid('email') && !hasBackend) || !this.validator.isTouched('email');
        },
        isValidPhone() {
            const hasBackendPhone = this.backendErrors.phone && this.backendErrors.phone.length;
            const hasBackendPrefix = this.backendErrors.phone_prefix && this.backendErrors.phone_prefix.length;

            const okPhone = this.validator.isValid('phone') && !hasBackendPhone;
            const okPrefix = this.validator.isValid('phone_prefix') && !hasBackendPrefix;

            return (okPhone && okPrefix) || (!this.validator.isTouched('phone') && !this.validator.isTouched('phone_prefix'));
        },
        isTaxIdOrCompanyNameSet() {
            return (!!this.localIdentity.company_name && this.localIdentity.company_name.trim().length > 0)
                || (!!this.localIdentity.tax_id && this.localIdentity.tax_id.trim().length > 0);
        },
        dobMaxDate() {
            const date = new Date();
            date.setFullYear(date.getFullYear() - 18);
            return date;
        }
    },
    watch: {
        'localIdentity.country_id': function () {
            this.onTaxRelatedFieldChanged();
        },
        'localIdentity.company_name': function () {
            this.onTaxRelatedFieldChanged();
        },
        'localIdentity.tax_id': function () {
            this.onTaxRelatedFieldChanged();
        }
    }
}
</script>
<template>
    <div class="component-identity-add-edit">
        <div class="row mb-3" v-if="actionType !== 'firstAdd'">
            <div class="col-12">
                <div class="input-group" :class="{'is-invalid': !isValidName}">
                    <span class="input-group-text">
                        <i class="fas fa-tag fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true, 'is-invalid': !isValidName}">
                        <input type="text" class="form-control" id="name" name="name"
                               v-model="localIdentity.name" @focusout="validator.setTouched('name')"
                               :class="{'is-invalid': !isValidName}"
                               :readonly="actionType === 'firstAdd' || (actionType === 'edit' && localIdentity.id === null)"
                               :placeholder="$translate('checkout.identity_name')">
                        <label for="name">{{ $translate('checkout.identity_name') }}</label>
                    </div>
                </div>
                <template v-if="!isValidName">
                    <div v-for="msg in allMessages('name')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-6 mb-3 mb-sm-0">
                <div class="input-group" :class="{'is-invalid': !isValidFirstName}">
                    <span class="input-group-text">
                        <i class="fas fa-user pull-right fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="first_name" name="first_name"
                               v-model="localIdentity.first_name" @focusout="validator.setTouched('first_name')"
                               :class="{'is-invalid': !isValidFirstName}"
                               :placeholder="$translate('auth.first_name')">
                        <label for="first_name">{{ $translate('auth.first_name') }}</label>
                    </div>
                </div>
                <template v-if="!isValidFirstName">
                    <div v-for="msg in allMessages('first_name')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-sm-6">
                <div class="input-group" :class="{'is-invalid': !isValidLastName}">
                    <span class="input-group-text">
                        <i class="fas fa-user pull-right fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="last_name" name="last_name"
                               v-model="localIdentity.last_name" @focusout="validator.setTouched('last_name')"
                               :class="{'is-invalid': !isValidLastName}"
                               :placeholder="$translate('auth.last_name')">
                        <label for="last_name">{{ $translate('auth.last_name') }}</label>
                    </div>
                </div>
                <template v-if="!isValidLastName">
                    <div v-for="msg in allMessages('last_name')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="mb-3">
            <div class="pretty p-default">
                <input type="checkbox" v-model="isCompanyAddress" @change="toggleCompanyAddress">
                <div class="state p-primary">
                    <label>{{ $translate('checkout.company_address') }}</label>
                </div>
            </div>
        </div>
        <div class="row mb-3" v-if="isCompanyAddress">
            <div class="col-sm-6 mb-3 mb-sm-0">
                <div class="input-group" :class="{'is-invalid': !isValidCompanyName}">
                    <span class="input-group-text">
                        <i class="fas fa-building fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="company_name"
                            name="company_name"
                            v-model="localIdentity.company_name" @focusout="validator.setTouched('company_name')"
                            :class="{'is-invalid': !isValidCompanyName}"
                            :placeholder="$translate('auth.company_name')">
                        <label for="company_name">{{ $translate('auth.company_name') }}</label>
                    </div>
                </div>
                <template v-if="!isValidCompanyName">
                    <div v-for="msg in allMessages('company_name')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-sm-6">
                <div class="input-group" :class="{'is-invalid': !isValidTaxId}">
                    <span class="input-group-text">
                        <i class="fas fa-money-check fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true, 'is-invalid': !isValidTaxId}">
                        <input type="text" class="form-control" id="tax_id" name="tax_id"
                               v-model="localIdentity.tax_id" @focusout="validator.setTouched('tax_id')"
                               :class="{'is-invalid': !isValidTaxId}"
                               :placeholder="$translate('auth.tax_id')">
                        <label for="tax_id">{{ $translate('auth.tax_id') }}</label>
                    </div>
                </div>
                <template v-if="!isValidTaxId">
                    <div v-for="msg in allMessages('tax_id')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-6 mb-3 mb-sm-0">
                <div class="input-group" :class="{'is-invalid': !isValidAddress}">
                    <span class="input-group-text">
                        <i class="fas fa-map-marker-alt fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="address" name="address"
                               v-model="localIdentity.address" @focusout="validator.setTouched('address')"
                               :class="{'is-invalid': !isValidAddress}"
                               :placeholder="$translate('auth.address_street')">
                        <label for="address">{{ $translate('auth.address_street') }}</label>
                    </div>
                </div>
                <template v-if="!isValidAddress">
                    <div v-for="msg in allMessages('address')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-sm-6">
                <div class="input-group" :class="{'is-invalid': !isValidAddress2}">
                    <span class="input-group-text">
                        <i class="fas fa-map-marker-alt fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="address2" name="address2"
                               v-model="localIdentity.address2" @focusout="validator.setTouched('address2')"
                               :class="{'is-invalid': !isValidAddress2}"
                               :placeholder="$translate('auth.address_house_apartment_number')">
                        <label for="address2">{{ $translate('auth.address_house_apartment_number') }}</label>
                    </div>
                </div>
                <template v-if="!isValidAddress2">
                    <div v-for="msg in allMessages('address2')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-6 mb-3 mb-sm-0">
                <div class="input-group" :class="{'is-invalid': !isValidDistrict}">
                    <span class="input-group-text">
                        <i class="fas fa-map-marker-alt fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="district" name="district"
                               v-model="localIdentity.district" @focusout="validator.setTouched('district')"
                               :class="{'is-invalid': !isValidDistrict}"
                               :placeholder="$translate('auth.district')">
                        <label for="district">{{ $translate('auth.district') }}</label>
                    </div>
                </div>
                <template v-if="!isValidDistrict">
                    <div v-for="msg in allMessages('district')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-sm-6">
                <div class="input-group" :class="{'is-invalid': !isValidPostalCode}">
                    <span class="input-group-text">
                        <i class="fas fa-mail-bulk fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="postal_code"
                            name="postal_code"
                            v-model="localIdentity.postal_code" @focusout="validator.setTouched('postal_code')"
                            :class="{'is-invalid': !isValidPostalCode}"
                            :placeholder="$translate('auth.postal_code')">
                        <label for="postal_code">{{ $translate('auth.postal_code') }}</label>
                    </div>
                </div>
                <template v-if="!isValidPostalCode">
                    <div v-for="msg in allMessages('postal_code')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-6 mb-3 mb-sm-0">
                <div :class="{'is-invalid': !isValidCountryId}">
                    <SelectCountry
                        :selected-id="localIdentity.country_id ? Number(localIdentity.country_id) : null"
                        @onSelected="selectCountry" />
                </div>
                <template v-if="!isValidCountryId">
                    <div v-for="msg in allMessages('country_id')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-sm-6">
                <div class="input-group" :class="{'is-invalid': !isValidCity}">
                    <span class="input-group-text">
                        <i class="fas fa-city fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true}">
                        <input type="text" class="form-control" id="city" name="city"
                               v-model="localIdentity.city" @focusout="validator.setTouched('city')"
                               :class="{'is-invalid': !isValidCity}"
                               :placeholder="$translate('auth.city')">
                        <label for="city">{{ $translate('auth.city') }}</label>
                    </div>
                </div>
                <template v-if="!isValidCity">
                    <div v-for="msg in allMessages('city')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3" v-if="actionType === 'firstAdd' || (actionType === 'edit' && localIdentity.id === null)">
            <div class="col-12 mb-3">
                <div :class="{'is-invalid': !isValidPhone}">
                    <SelectPhone
                        :selected="localIdentity.phone"
                        @onSelected="updatePhone"
                        @onPrefixTouched="onPhoneTouched('prefix')"
                        @onPhoneTouched="onPhoneTouched('phone')"></SelectPhone>
                </div>
                <template v-if="!isValidPhone">
                    <div v-for="msg in allMessages('phone')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <div class="row mb-3" v-if="!(actionType === 'firstAdd' || (actionType === 'edit' && localIdentity.id === null))">
            <div class="col-12 mb-3">
                <div :class="{'is-invalid': !isValidPhone}">
                    <SelectPhone
                        :selected="localIdentity.phone"
                        :isInvalid="!isValidPhone"
                        @onSelected="updatePhone"
                        @onPrefixTouched="onPhoneTouched('prefix')"
                        @onPhoneTouched="onPhoneTouched('phone')"></SelectPhone>
                </div>
                <template v-if="!isValidPhone">
                    <div v-for="msg in allMessages('phone')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
            <div class="col-12 md-3">
                <div class="input-group" :class="{'is-invalid': !isValidEmail}">
                    <span class="input-group-text">
                        <i class="fas fa-envelope fa-fw"></i>
                    </span>
                    <div :class="{'form-floating': true, 'is-invalid': !isValidEmail}">
                        <input type="text" class="form-control" id="email" name="email"
                               v-model="localIdentity.email" @focusout="validator.setTouched('email')"
                               :class="{'is-invalid': !isValidEmail}"
                               :readonly="actionType === 'firstAdd' || (actionType === 'edit' && localIdentity.id === null)"
                               :placeholder="$translate('auth.email_address')">
                        <label for="email">{{ $translate('auth.email_address') }}</label>
                    </div>
                </div>
                <template v-if="!isValidEmail">
                    <div v-for="msg in allMessages('email')" class="invalid-feedback d-block">
                        {{ msg }}
                    </div>
                </template>
            </div>
        </div>

        <template v-if="showField('id_number') || showField('dob') || showField('pob')">
            <div class="row mb-2">
                <p class="mb-0">
                    <small class="text-muted">
                        {{ $translate('checkout.following_fields_are_only_required_when_registering_this_domain_for_individuals') }}
                    </small>
                </p>
            </div>
            <div class="row">
                <div class="col-sm-6 mb-3" v-if="showField('id_number')">
                    <div class="input-group" :class="{'is-invalid': !isValidIdNumber}">
                        <div class="input-group-text">
                            <i class="fas fa-id-card fa-fw"></i>
                        </div>
                        <div class="form-floating">
                            <input type="text" class="form-control" id="id_number" name="id_number"
                                   v-model="localIdentity.id_number" @focusout="validator.setTouched('id_number')"
                                   :class="{'is-invalid': !isValidIdNumber}"
                                   :placeholder="$translate('manager-settings.id_number')">
                            <label for="id_number">{{ $translate('manager-settings.id_number') }}</label>
                        </div>
                    </div>
                    <template v-if="!isValidIdNumber">
                        <div v-for="msg in allMessages('id_number')" class="invalid-feedback d-block">
                            {{ msg }}
                        </div>
                    </template>
                </div>
                <div class="col-sm-6 mb-3" v-if="showField('dob')">
                    <div class="input-group mb-sm-0 flex-nowrap" :class="{'is-invalid': !isValidDob}">
                        <div class="input-group-text">
                            <i class="fas fa-calendar fa-fw"></i>
                        </div>
                        <div class="flex-grow-1">
                            <div class="form-floating static" :class="{'is-invalid': !isValidDob, 'is-active': localIdentity.dob}">
                                <VueDatePicker
                                    v-model="localIdentity.dob"
                                    model-type="yyyy-MM-dd"
                                    :text-input="true"
                                    :auto-apply="true"
                                    :year-first="true"
                                    :max-date="dobMaxDate"
                                    :time-config="{enableTimePicker:false}"
                                    :formats="{preview:'yyyy-MM-dd', input:'yyyy-MM-dd'}"
                                    :input-attrs="{hideInputIcon: true}"
                                    :placeholder="$translate('manager-settings.date_of_birth')"></VueDatePicker>
                                <label for="dob">{{ $translate('manager-settings.date_of_birth') }}</label>
                            </div>
                        </div>
                    </div>
                    <template v-if="!isValidDob">
                        <div v-for="msg in allMessages('dob')" class="invalid-feedback d-block">
                            {{ msg }}
                        </div>
                    </template>
                </div>
                <div class="col-sm-6 mb-3" v-if="showField('pob')">
                    <div :class="{'is-invalid': !isValidPob}">
                        <SelectCountry
                            :selected-id="localIdentity.pob ? Number(localIdentity.pob) : null"
                            @onSelected="selectPob"
                            :placeholder="$translate('manager-settings.place_of_birth')" />
                    </div>
                    <template v-if="!isValidPob">
                        <div v-for="msg in allMessages('pob')" class="invalid-feedback d-block">
                            {{ msg }}
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </div>
    <div class="component-footer-wrapper" v-if="actionType !== 'firstAdd'">
        <div class="row component-footer">
            <div class="col-6">
                <button class="btn btn-circle w-100" @click="onDismiss()">{{ $translate('checkout.cancel') }}</button>
            </div>
            <div class="col-6">
                <button class="btn btn-primary w-100" @click="save()" :class="{disabled: saving}">
                    <template v-if="!saving">{{ $translate('checkout.save') }}</template>
                    <div v-if="saving" class="vue-loader"></div>
                </button>
            </div>
        </div>
    </div>
</template>
